import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, GoldButton } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Zap, Clock, Hand } from "lucide-react";

const MODE_META = {
  immediate: { icon: Zap, label: "Imediato", hint: "Sem timeout — se o motorista rejeitar, envia imediatamente ao próximo." },
  timeout: { icon: Clock, label: "Com timeout", hint: "Se o motorista não responder dentro do tempo definido, envia ao próximo." },
  manual: { icon: Hand, label: "Manual", hint: "Admin escolhe manualmente cada motorista — sem rotação automática." },
};

const RULE_META = {
  proximity: { label: "Proximidade", hint: "Motoristas com base perto do local de recolha primeiro" },
  rating: { label: "Classificação", hint: "Motoristas com rating mais alto primeiro" },
  workload: { label: "Carga de trabalho", hint: "Motoristas com menos viagens este período primeiro" },
};

export default function DispatchSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/dispatch/config").then((r) => setCfg(r.data));
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="p-10 text-slate-500">A carregar…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/dispatch/config", cfg);
      toast.success("Configuração de dispatch guardada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const moveRule = (index, dir) => {
    const rules = [...cfg.order_rules];
    const j = index + dir;
    if (j < 0 || j >= rules.length) return;
    [rules[index], rules[j]] = [rules[j], rules[index]];
    setCfg({ ...cfg, order_rules: rules });
  };

  return (
    <div className="p-10 max-w-[1200px]">
      <PageHeader
        eyebrow="Iter 23 · Msg 679"
        title="Dispatch de Motoristas"
        description="Configuração do envio automático de reservas por WhatsApp aos motoristas — com botões Aceitar/Rejeitar e rotação automática."
      >
        <GoldButton onClick={save} disabled={saving} data-testid="save-dispatch">
          {saving ? "A guardar…" : "Guardar configuração"}
        </GoldButton>
      </PageHeader>

      {/* Modo */}
      <div className="border border-slate-200 bg-white p-8 mb-6">
        <p className="label-caps mb-4">Modo de envio</p>
        <div className="grid grid-cols-3 gap-4">
          {Object.entries(MODE_META).map(([k, meta]) => {
            const Icon = meta.icon;
            const active = cfg.mode === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setCfg({ ...cfg, mode: k })}
                data-testid={`dispatch-mode-${k}`}
                className={`text-left p-5 border transition-colors ${active ? "border-[#f59e0b] bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}
              >
                <div className={`flex items-center gap-3 mb-2 ${active ? "text-[#f59e0b]" : "text-slate-700"}`}>
                  <Icon className="w-5 h-5" />
                  <div className="font-semibold text-[#0a0f1e]">{meta.label}</div>
                </div>
                <div className="text-xs text-slate-600">{meta.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Timeout */}
      {cfg.mode === "timeout" && (
        <div className="border border-slate-200 bg-white p-8 mb-6">
          <p className="label-caps mb-4">Timeout (minutos)</p>
          <div className="flex items-center gap-4 max-w-md">
            <Input
              type="number" min="1" max="60"
              value={cfg.timeout_min}
              onChange={(e) => setCfg({ ...cfg, timeout_min: Math.max(1, Number(e.target.value) || 5) })}
              data-testid="dispatch-timeout"
              className="input-dt h-12 w-28 text-lg font-mono text-center"
            />
            <div className="text-sm text-slate-600">
              Se o motorista não responder em {cfg.timeout_min} min, a reserva rota automaticamente para o próximo motorista.
            </div>
          </div>
        </div>
      )}

      {/* Ordem de regras */}
      <div className="border border-slate-200 bg-white p-8 mb-6">
        <div className="flex items-center justify-between mb-4">
          <p className="label-caps">Ordem das regras de rotação</p>
          <div className="text-xs text-slate-500">Do mais importante para o menos</div>
        </div>
        <div className="space-y-2">
          {cfg.order_rules.map((r, i) => {
            const meta = RULE_META[r] || { label: r, hint: "" };
            return (
              <div
                key={r}
                data-testid={`rule-${r}`}
                className="flex items-center gap-4 border border-slate-200 p-4 bg-white"
              >
                <div className="w-8 h-8 border border-[#f59e0b] text-[#f59e0b] flex items-center justify-center font-display text-sm font-bold">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-[#0a0f1e]">{meta.label}</div>
                  <div className="text-xs text-slate-600">{meta.hint}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    disabled={i === 0}
                    onClick={() => moveRule(i, -1)}
                    data-testid={`rule-up-${r}`}
                    className="p-1 border border-slate-200 hover:border-[#f59e0b] disabled:opacity-30"
                  >
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    disabled={i === cfg.order_rules.length - 1}
                    onClick={() => moveRule(i, 1)}
                    data-testid={`rule-down-${r}`}
                    className="p-1 border border-slate-200 hover:border-[#f59e0b] disabled:opacity-30"
                  >
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 text-[11px] text-slate-500 italic">
          Nota: quando o WhatsApp Twilio estiver configurado (variáveis TWILIO_*), as mensagens deixam de ser simuladas e passam a ser entregues com botões Aceitar/Rejeitar interactivos.
        </div>
      </div>

      {/* Iter 25 Sub-Fase E: Low-payout alerts */}
      <div className="border border-slate-200 bg-white p-8">
        <p className="label-caps mb-4">Alerta de payout baixo</p>
        <p className="text-xs text-slate-600 mb-4">
          O motorista é avisado quando um dispatch tem payout inferior ao normal — pode aceitar ou rejeitar sem penalização.
        </p>
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div>
            <label className="label-caps block mb-2">% da média do motorista</label>
            <div className="flex items-center gap-2">
              <Input
                type="number" min="0" max="100"
                value={cfg.low_payout_min_pct ?? 70}
                onChange={(e) => setCfg({ ...cfg, low_payout_min_pct: Number(e.target.value) })}
                data-testid="low-payout-pct"
                className="input-dt h-11 w-24 text-center font-mono"
              />
              <span className="text-sm text-slate-600">%</span>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">Ex: 70% = alertar se payout menor que 70% da média</div>
          </div>
          <div>
            <label className="label-caps block mb-2">Valor absoluto mínimo (€)</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">€</span>
              <Input
                type="number" min="0" step="0.5"
                value={cfg.low_payout_min_absolute ?? 20}
                onChange={(e) => setCfg({ ...cfg, low_payout_min_absolute: Number(e.target.value) })}
                data-testid="low-payout-abs"
                className="input-dt h-11 w-24 text-center font-mono"
              />
            </div>
            <div className="text-[10px] text-slate-500 mt-1">Ex: alertar se payout menor que 20€</div>
          </div>
        </div>
      </div>
    </div>
  );
}
